import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router';
import { RequirePermission } from '@/components/workspace/RequirePermission';
import { SamplesGate } from '@/components/workspace/SamplesGate';
import { Loader } from '@/components/Loader';
import { AppLayout } from '@/layouts/AppLayout';
import { GuestLayout } from '@/layouts/GuestLayout';
import { DEFAULT_SOURCES_ROUTE, LEGACY_SOURCES_PATH } from '@/pages/DataStorages/groups';
import { RECEIVERS_PATH } from '@/pages/Integrations/Targets/paths';
import { DEFAULT_LLM_GATE_TAB, LLM_GATE_TABS } from '@/pages/LlmGate/tabs';
import { FeatureGate } from '@/router/FeatureGate';
import { AdminRoute, GuestRoute, ProtectedRoute } from '@/router/guards';
import { NotFound } from '@/router/NotFound';
import { LegacyAssetRedirect } from '@/pages/ServiceCatalog/LegacyAssetRedirect';

/* ── Auth ── */
const SignIn = lazy(() => import('@/pages/SignIn'));
const SignUp = lazy(() => import('@/pages/SignUp'));
const SignUpVerification = lazy(() => import('@/pages/SignUpVerification'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const AcceptInvitation = lazy(() => import('@/pages/AcceptInvitation'));
const ConnectAws = lazy(() => import('@/pages/ConnectAws'));
const SsoFailed = lazy(() => import('@/pages/SsoFailed'));
const AwsError = lazy(() => import('@/pages/AwsErrors'));

/* ── Основные страницы ── */
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const ServiceCatalog = lazy(() => import('@/pages/ServiceCatalog'));
const ServiceDetail = lazy(() => import('@/pages/ServiceCatalog/ServiceDetail'));
const CreateExternalConnection = lazy(
	() => import('@/pages/ServiceCatalog/CreateExternalConnection'),
);
const ApiEndpoints = lazy(() => import('@/pages/ApiEndpoints'));
const ApiSamplePage = lazy(() => import('@/pages/ApiEndpoints/SamplePage'));
const EndpointCard = lazy(() => import('@/pages/ApiEndpoints/EndpointCard'));
const DataStorages = lazy(() => import('@/pages/DataStorages'));
const StorageCatalog = lazy(() => import('@/pages/DataStorages/StorageCatalog'));
const DataTypes = lazy(() => import('@/pages/DataTypes'));
const DataTypeDetail = lazy(() => import('@/pages/DataTypes/DataTypeDetail'));
const Policies = lazy(() => import('@/pages/Policies'));
const PolicyEditor = lazy(() => import('@/pages/Policies/PolicyEditor'));
const NewPolicyEditor = lazy(() => import('@/pages/Policies/NewPolicyEditor'));
const Events = lazy(() => import('@/pages/Events'));
const EventPage = lazy(() => import('@/pages/Events/EventPage'));
const EventSamplePage = lazy(() => import('@/pages/Events/EventSamplePage'));
const Integrations = lazy(() => import('@/pages/Integrations'));
const SlackSettings = lazy(() => import('@/pages/Integrations/SlackSettings'));
const JiraSettings = lazy(() => import('@/pages/Integrations/JiraSettings'));
const WebhookSettings = lazy(() => import('@/pages/Integrations/WebhookSettings'));
const ExternalApiSettings = lazy(() => import('@/pages/Integrations/ExternalApiSettings'));
const IntegrationTargetNew = lazy(() => import('@/pages/Integrations/Targets/TargetNewPage'));
const IntegrationTargetDetails = lazy(() => import('@/pages/Integrations/Targets/TargetDetails'));
const Sensors = lazy(() => import('@/pages/Sensors'));
const AwsAccountDetail = lazy(() => import('@/pages/Sensors/AwsAccountDetail'));
const Team = lazy(() => import('@/pages/Team'));
const Admin = lazy(() => import('@/pages/Admin'));
const AssetGroups = lazy(() => import('@/pages/AssetGroups'));
const AssetGroupDetail = lazy(() => import('@/pages/AssetGroups/AssetGroupDetail'));
const ChangePassword = lazy(() => import('@/pages/ChangePassword'));
const Account = lazy(() => import('@/pages/Account'));
const AiCopilot = lazy(() => import('@/pages/AiCopilot'));
const AttackPaths = lazy(() => import('@/pages/AttackPaths'));
const AttackPathDetail = lazy(() => import('@/pages/AttackPaths/PathDetail'));
const DataMap = lazy(() => import('@/pages/DataMap'));
const Reports = lazy(() => import('@/pages/Reports'));
const ReportPage = lazy(() => import('@/pages/Reports/ReportPage'));
const Learning = lazy(() => import('@/pages/Learning'));

/* ── LLM-шлюз ── */
const LlmGate = lazy(() => import('@/pages/LlmGate'));

/* ── Data Storage details ── */
const S3BucketDetail = lazy(() => import('@/pages/DataStorages/details/S3BucketDetail'));
const S3ClusterPage = lazy(() => import('@/pages/DataStorages/details/S3ClusterPage'));
const KafkaInstanceDetail = lazy(() => import('@/pages/DataStorages/details/KafkaInstanceDetail'));
const KafkaTopicDetail = lazy(() => import('@/pages/DataStorages/details/KafkaTopicDetail'));
const KafkaSchemaPage = lazy(() => import('@/pages/DataStorages/details/KafkaSchemaPage'));
const DatabaseDetail = lazy(() => import('@/pages/DataStorages/details/DatabaseDetail'));
const NoSqlDetail = lazy(() => import('@/pages/DataStorages/details/NoSqlDetail'));
const NoSqlCollectionPage = lazy(() => import('@/pages/DataStorages/details/NoSqlCollectionPage'));
const NoSqlSchemaPage = lazy(() => import('@/pages/DataStorages/details/NoSqlSchemaPage'));

/* ── Sample viewers ── */
const S3SamplePage = lazy(() => import('@/pages/DataStorages/samples/S3SamplePage'));
const KafkaSamplePage = lazy(() => import('@/pages/DataStorages/samples/KafkaSamplePage'));
const DatabaseSamplePage = lazy(() => import('@/pages/DataStorages/samples/DatabaseSamplePage'));
const NoSqlSamplePage = lazy(() => import('@/pages/DataStorages/samples/NoSqlSamplePage'));

/** Обёртка Suspense с центрированным лоадером */
function LazyPage({ children }: { children: React.ReactNode }) {
	return (
		<Suspense
			fallback={
				<div className="flex h-full items-center justify-center" data-page-loading="">
					<Loader size={24} />
				</div>
			}
		>
			{children}
		</Suspense>
	);
}

/**
 * Редирект прежнего адреса с сохранением параметров. Источники сканирования: по
 * параметрам приходит подсветка карточки (HIGHLIGHT_PARAM из ссылки конфликта в
 * DataSourceForm.tsx). Вход и регистрация: gateway шлёт на /sign-in?email= и
 * /sign-up?<параметры подписки AWS Marketplace> (AwsMarketplaceService.createRedirectUrl).
 * Образец портала /data-catalog/sample?id=&direction= и ссылка уведомлений сервера
 * /api-trafficPaths/sample?id=&direction= — тот же образец эндпоинта.
 * Потерять параметры значило бы привести человека не туда, куда его вела ссылка.
 */
function RedirectKeepingSearch({ to }: { to: string }) {
	const { search } = useLocation();
	return <Navigate to={`${to}${search}`} replace />;
}

export function AppRoutes() {
	return (
		<Routes>
			{/* ══════ Гостевые роуты ══════ */}
			<Route element={<GuestRoute />}>
				<Route element={<GuestLayout />}>
					<Route
						path="/signin"
						element={
							<LazyPage>
								<SignIn />
							</LazyPage>
						}
					/>
					<Route
						path="/signup"
						element={
							<LazyPage>
								<SignUp />
							</LazyPage>
						}
					/>
					<Route
						path="/sign-up-verification/:secret"
						element={
							<LazyPage>
								<SignUpVerification />
							</LazyPage>
						}
					/>
					<Route
						path="/reset-password"
						element={
							<LazyPage>
								<ResetPassword />
							</LazyPage>
						}
					/>
					<Route
						path="/accept-invite"
						element={
							<LazyPage>
								<AcceptInvitation variant="invite" />
							</LazyPage>
						}
					/>
					<Route
						path="/accept-enrollment"
						element={
							<LazyPage>
								<AcceptInvitation variant="enrollment" />
							</LazyPage>
						}
					/>
					<Route
						path="/connect-aws"
						element={
							<LazyPage>
								<FeatureGate flag="awsIntegration" fallback="/signin">
									<ConnectAws />
								</FeatureGate>
							</LazyPage>
						}
					/>
					<Route
						path="/sso-failed"
						element={
							<LazyPage>
								<FeatureGate flag="sso" fallback="/signin">
									<SsoFailed />
								</FeatureGate>
							</LazyPage>
						}
					/>
					{/* Оба прежних адреса ошибок gateway — один экран #aws-error макета */}
					{['/aws-non-existent-subscription', '/aws-something-wrong'].map((path) => (
						<Route
							key={path}
							path={path}
							element={
								<LazyPage>
									<FeatureGate flag="awsIntegration" fallback="/signin">
										<AwsError />
									</FeatureGate>
								</LazyPage>
							}
						/>
					))}
				</Route>
			</Route>

			{/* ══════ Защищённые роуты ══════ */}
			<Route element={<ProtectedRoute />}>
				<Route element={<AppLayout />}>
					<Route
						path="/overview"
						element={
							<LazyPage>
								<Dashboard />
							</LazyPage>
						}
					/>
					<Route
						path="/data-map"
						element={
							<LazyPage>
								<DataMap />
							</LazyPage>
						}
					/>

					{/* Каталог сервисов */}
					<Route path="/service-catalog">
						<Route index element={<Navigate to="services" replace />} />
						<Route
							path="services"
							element={
								<LazyPage>
									<ServiceCatalog />
								</LazyPage>
							}
						/>
						<Route
							path="external-connections"
							element={
								<LazyPage>
									<ServiceCatalog />
								</LazyPage>
							}
						/>
						{/* Внешнее подключение создаёт право изменения инвентаря */}
						<Route element={<RequirePermission permission="inventory.write" />}>
							<Route
								path="external-connections/new"
								element={
									<LazyPage>
										<CreateExternalConnection />
									</LazyPage>
								}
							/>
						</Route>
						<Route
							path="services/:id/:tab?/:detailsWith?/:flowDirection?"
							element={
								<LazyPage>
									<ServiceDetail />
								</LazyPage>
							}
						/>
					</Route>

					{/* API эндпоинты */}
					<Route path="/api-endpoints">
						<Route
							index
							element={
								<LazyPage>
									<ApiEndpoints />
								</LazyPage>
							}
						/>
						<Route
							path="sample"
							element={
								<LazyPage>
									<SamplesGate>
										<ApiSamplePage />
									</SamplesGate>
								</LazyPage>
							}
						/>
						{/* Карточка эндпоинта (#api/<id> макета): чтения инвентаря достаточно */}
						<Route
							path=":id"
							element={
								<LazyPage>
									<EndpointCard />
								</LazyPage>
							}
						/>
					</Route>

					{/* Хранилища данных */}
					<Route path="/data-storages">
						{/* Все хранилища одним каталогом (E3.1); подробные списки — на вкладках видов */}
						<Route
							index
							element={
								<LazyPage>
									<StorageCatalog />
								</LazyPage>
							}
						/>
						{/* S3 */}
						<Route
							path="s3-buckets/sources"
							element={
								<LazyPage>
									<DataStorages />
								</LazyPage>
							}
						/>
						<Route
							path="s3-buckets"
							element={
								<LazyPage>
									<DataStorages />
								</LazyPage>
							}
						/>
						<Route
							path="s3-buckets/:id/:tab?"
							element={
								<LazyPage>
									<S3BucketDetail />
								</LazyPage>
							}
						/>
						<Route
							path="s3-buckets/:id/clusters/:clusterId"
							element={
								<LazyPage>
									<S3ClusterPage />
								</LazyPage>
							}
						/>
						<Route
							path="s3-buckets/sample/:sampleId"
							element={
								<LazyPage>
									<SamplesGate>
										<S3SamplePage />
									</SamplesGate>
								</LazyPage>
							}
						/>
						{/* Kafka */}
						<Route
							path="kafka/sources"
							element={
								<LazyPage>
									<DataStorages />
								</LazyPage>
							}
						/>
						<Route
							path="kafka/instances"
							element={
								<LazyPage>
									<DataStorages />
								</LazyPage>
							}
						/>
						<Route
							path="kafka/instances/:id/:tab?"
							element={
								<LazyPage>
									<KafkaInstanceDetail />
								</LazyPage>
							}
						/>
						<Route
							path="kafka/topics"
							element={
								<LazyPage>
									<DataStorages />
								</LazyPage>
							}
						/>
						<Route
							path="kafka/topics/:id/:tab?"
							element={
								<LazyPage>
									<KafkaTopicDetail />
								</LazyPage>
							}
						/>
						<Route
							path="kafka/topics/:topicId/schemas/:schemaId"
							element={
								<LazyPage>
									<KafkaSchemaPage />
								</LazyPage>
							}
						/>
						<Route
							path="kafka/sample/:sampleId"
							element={
								<LazyPage>
									<SamplesGate>
										<KafkaSamplePage />
									</SamplesGate>
								</LazyPage>
							}
						/>
						{/* SQL Databases */}
						<Route
							path="databases/sources"
							element={
								<LazyPage>
									<DataStorages />
								</LazyPage>
							}
						/>
						<Route
							path="databases"
							element={
								<LazyPage>
									<DataStorages />
								</LazyPage>
							}
						/>
						<Route
							path="databases/instances"
							element={
								<LazyPage>
									<DataStorages />
								</LazyPage>
							}
						/>
						<Route
							path="databases/:id/:tab?"
							element={
								<LazyPage>
									<DatabaseDetail />
								</LazyPage>
							}
						/>
						<Route
							path="databases/sample/:sampleId"
							element={
								<LazyPage>
									<SamplesGate>
										<DatabaseSamplePage />
									</SamplesGate>
								</LazyPage>
							}
						/>
						{/* NoSQL */}
						<Route
							path="nosql-databases/sources"
							element={
								<LazyPage>
									<DataStorages />
								</LazyPage>
							}
						/>
						<Route
							path="nosql-databases"
							element={
								<LazyPage>
									<DataStorages />
								</LazyPage>
							}
						/>
						<Route
							path="nosql-databases/instances"
							element={
								<LazyPage>
									<DataStorages />
								</LazyPage>
							}
						/>
						<Route
							path="nosql-databases/:id/:tab?"
							element={
								<LazyPage>
									<NoSqlDetail />
								</LazyPage>
							}
						/>
						<Route
							path="nosql-databases/collections/:id/:tab?"
							element={
								<LazyPage>
									<NoSqlCollectionPage />
								</LazyPage>
							}
						/>
						<Route
							path="nosql-databases/schemas/:id"
							element={
								<LazyPage>
									<NoSqlSchemaPage />
								</LazyPage>
							}
						/>
						<Route
							path="nosql-databases/sample/:id"
							element={
								<LazyPage>
									<SamplesGate>
										<NoSqlSamplePage />
									</SamplesGate>
								</LazyPage>
							}
						/>
					</Route>

					{/* Прежний адрес источников сканирования DAR: раздела больше нет,
					    источники стали вкладкой внутри хранилищ данных. Редирект —
					    ради закладок и ссылки конфликта в DataSourceForm.tsx, которая
					    приходит сюда, когда тип найденной карточки на момент перехода
					    неизвестен (см. sourceHighlightPath в DataStorages/groups.ts). */}
					<Route
						path={LEGACY_SOURCES_PATH}
						element={<RedirectKeepingSearch to={DEFAULT_SOURCES_ROUTE} />}
					/>

					{/* Типы данных */}
					<Route path="/data-types">
						<Route
							index
							element={
								<LazyPage>
									<DataTypes />
								</LazyPage>
							}
						/>
						<Route
							path=":id"
							element={
								<LazyPage>
									<DataTypeDetail />
								</LazyPage>
							}
						/>
					</Route>

					{/* Политики: список, новая политика (#policies/new макета) и сохранённая */}
					<Route path="/policies">
						<Route
							index
							element={
								<LazyPage>
									<Policies />
								</LazyPage>
							}
						/>
						<Route element={<RequirePermission permission="policies.write" />}>
							<Route
								path="new"
								element={
									<LazyPage>
										<NewPolicyEditor />
									</LazyPage>
								}
							/>
						</Route>
						<Route
							path=":id"
							element={
								<LazyPage>
									<PolicyEditor />
								</LazyPage>
							}
						/>
					</Route>

					{/* Журнал событий и страница события (eventsPage макета, #events/<id>) */}
					<Route
						path="/activity-log"
						element={
							<LazyPage>
								<Events />
							</LazyPage>
						}
					/>
					<Route
						path="/activity-log/:eventId"
						element={
							<LazyPage>
								<EventPage />
							</LazyPage>
						}
					/>
					<Route
						path="/activity-log/:eventId/sample"
						element={
							<LazyPage>
								<SamplesGate>
									<EventSamplePage />
								</SamplesGate>
							</LazyPage>
						}
					/>

					{/* Интеграции */}
					<Route path="/integrations">
						<Route
							index
							element={
								<LazyPage>
									<Integrations />
								</LazyPage>
							}
						/>
						{/* Настройки каналов читает любой участник (контракты К:377), меняет администратор:
						    форма у остальных ролей видна неактивной с причиной, ключи External API
						    участнику не запрашиваются */}
						<Route
							path="slack"
							element={
								<LazyPage>
									<SlackSettings />
								</LazyPage>
							}
						/>
						<Route
							path="jira"
							element={
								<LazyPage>
									<JiraSettings />
								</LazyPage>
							}
						/>
						<Route
							path="webhook"
							element={
								<LazyPage>
									<WebhookSettings />
								</LazyPage>
							}
						/>
						<Route
							path="external-api"
							element={
								<LazyPage>
									<ExternalApiSettings />
								</LazyPage>
							}
						/>
						<Route
							path="external-api/new"
							element={
								<LazyPage>
									<ExternalApiSettings startCreating />
								</LazyPage>
							}
						/>
						{/* Получателей SIEM и MONQ видит и настраивает только администратор (API отвечает
						    403, решение владельца 25.10.2026): остальных адреса уводят на раздел */}
						<Route element={<AdminRoute redirectTo="/integrations" />}>
							{/* Список получателей — вкладка раздела; прежний адрес ведёт на неё */}
							<Route path="targets" element={<Navigate to={RECEIVERS_PATH} replace />} />
							<Route
								path="targets/new"
								element={
									<LazyPage>
										<IntegrationTargetNew />
									</LazyPage>
								}
							/>
							<Route
								path="targets/:id"
								element={
									<LazyPage>
										<IntegrationTargetDetails />
									</LazyPage>
								}
							/>
						</Route>
					</Route>

					{/* Инфраструктура и сенсоры */}
					<Route path="/infrastructure-access">
						<Route index element={<Navigate to="all" replace />} />
						<Route
							path="all"
							element={
								<LazyPage>
									<Sensors />
								</LazyPage>
							}
						/>
						<Route
							path="data-in-motion"
							element={
								<LazyPage>
									<Sensors />
								</LazyPage>
							}
						/>
						<Route
							path="data-at-rest"
							element={
								<LazyPage>
									<Sensors />
								</LazyPage>
							}
						/>
						{/* Адрес «<вкладка>/new» открывает окно подключения этого вида. Сенсоры DIM и DAR
						    выпускает только администратор; прямой адрес у остальных -- «Недостаточно прав» */}
						<Route element={<RequirePermission permission="administration.write" />}>
							<Route
								path="data-in-motion/new"
								element={
									<LazyPage>
										<Sensors />
									</LazyPage>
								}
							/>
							<Route
								path="data-at-rest/new"
								element={
									<LazyPage>
										<Sensors />
									</LazyPage>
								}
							/>
						</Route>
						<Route
							path="llm-gate"
							element={
								<LazyPage>
									<Sensors />
								</LazyPage>
							}
						/>
						{/* Токен LLM-шлюза выпускает только администратор */}
						<Route element={<AdminRoute />}>
							<Route
								path="llm-gate/new"
								element={
									<LazyPage>
										<Sensors />
									</LazyPage>
								}
							/>
						</Route>
						<Route
							path="aws"
							element={
								<LazyPage>
									<Sensors />
								</LazyPage>
							}
						/>
						<Route
							path="aws/:id"
							element={
								<LazyPage>
									<FeatureGate flag="awsIntegration" fallback="/infrastructure-access/all">
										<AwsAccountDetail />
									</FeatureGate>
								</LazyPage>
							}
						/>
					</Route>

					{/* LLM-шлюз: список вкладки и окно записи над ним (макет workspace-interactions.js:154-160);
					    просмотр -- всем, адреса изменения -- администратору */}
					<Route path="/llm-gate">
						<Route index element={<Navigate to={DEFAULT_LLM_GATE_TAB} replace />} />
						{LLM_GATE_TABS.filter((tab) => tab !== 'logs').map((tab) => (
							<Route
								key={tab}
								path={tab}
								element={
									<LazyPage>
										<LlmGate />
									</LazyPage>
								}
							/>
						))}
						<Route
							path="routes/:id"
							element={
								<LazyPage>
									<LlmGate />
								</LazyPage>
							}
						/>
						<Route
							path="policies/:id"
							element={
								<LazyPage>
									<LlmGate />
								</LazyPage>
							}
						/>
						<Route element={<AdminRoute redirectTo="/llm-gate/routes" />}>
							<Route
								path="logs"
								element={
									<LazyPage>
										<LlmGate />
									</LazyPage>
								}
							/>
						</Route>
						<Route element={<AdminRoute />}>
							<Route
								path="routes/new"
								element={
									<LazyPage>
										<LlmGate />
									</LazyPage>
								}
							/>
							<Route
								path="routes/:id/edit"
								element={
									<LazyPage>
										<LlmGate />
									</LazyPage>
								}
							/>
							<Route
								path="policies/new"
								element={
									<LazyPage>
										<LlmGate />
									</LazyPage>
								}
							/>
							<Route
								path="policies/:id/edit"
								element={
									<LazyPage>
										<LlmGate />
									</LazyPage>
								}
							/>
						</Route>
					</Route>

					{/* Команда, админка, группы активов */}
					<Route
						path="/team/:tab?"
						element={
							<LazyPage>
								<Team />
							</LazyPage>
						}
					/>
					{/* Настройки организации -- раздел администратора (макет pages-secondary.js) */}
					<Route element={<RequirePermission permission="administration.write" />}>
						<Route
							path="/admin"
							element={
								<LazyPage>
									<Admin />
								</LazyPage>
							}
						/>
					</Route>
					<Route path="/asset-groups">
						<Route
							index
							element={
								<LazyPage>
									<AssetGroups />
								</LazyPage>
							}
						/>
						<Route
							path=":id/:tab?"
							element={
								<LazyPage>
									<AssetGroupDetail />
								</LazyPage>
							}
						/>
					</Route>
					<Route
						path="/account"
						element={
							<LazyPage>
								<Account />
							</LazyPage>
						}
					/>
					<Route
						path="/change-password"
						element={
							<LazyPage>
								<ChangePassword />
							</LazyPage>
						}
					/>

					{/* Пути атак (feature flag: kubehound) */}
					<Route path="/attack-paths">
						<Route
							index
							element={
								<LazyPage>
									<FeatureGate flag="kubehound">
										<AttackPaths />
									</FeatureGate>
								</LazyPage>
							}
						/>
						<Route
							path=":pathId"
							element={
								<LazyPage>
									<FeatureGate flag="kubehound">
										<AttackPathDetail />
									</FeatureGate>
								</LazyPage>
							}
						/>
					</Route>

					{/* AI Copilot (feature flag) */}
					<Route
						path="/ai-copilot/:sessionId?"
						element={
							<LazyPage>
								<FeatureGate flag="aiCopilot">
									<AiCopilot />
								</FeatureGate>
							</LazyPage>
						}
					/>

					{/* Отчёты: список и страница отчёта (макет #reports/<id>) */}
					<Route path="/reports">
						<Route
							index
							element={
								<LazyPage>
									<Reports />
								</LazyPage>
							}
						/>
						<Route
							path=":reportId"
							element={
								<LazyPage>
									<ReportPage />
								</LazyPage>
							}
						/>
					</Route>

					{/* Обучение: уроки, задания и практика на учебном примере */}
					<Route
						path="/learning"
						element={
							<LazyPage>
								<Learning />
							</LazyPage>
						}
					/>

					{/* Неизвестный адрес -- пустое состояние в каркасе, как в макете */}
					<Route path="*" element={<NotFound />} />
				</Route>
			</Route>

			{/* ══════ Legacy-редиректы ══════ */}
			<Route path="/data-catalog" element={<Navigate to="/service-catalog/services" replace />} />
			<Route
				path="/data-catalog/assets/:id/:tab?/:detailsWith?/:flowDirection?"
				element={<LegacyAssetRedirect />}
			/>
			<Route
				path="/data-catalog/sample"
				element={<RedirectKeepingSearch to="/api-endpoints/sample" />}
			/>
			{/* Ссылка на образец в уведомлениях сервера (DimInformator.getEndpointLink) */}
			<Route
				path="/api-trafficPaths/sample"
				element={<RedirectKeepingSearch to="/api-endpoints/sample" />}
			/>
			<Route path="/sign-in" element={<RedirectKeepingSearch to="/signin" />} />
			<Route path="/sign-up" element={<RedirectKeepingSearch to="/signup" />} />

			{/* Корневой редирект */}
			<Route path="/" element={<Navigate to="/overview" replace />} />
		</Routes>
	);
}
